import { useInfiniteQuery, useQuery } from "react-query";
import { makeAuthenticatedRequest } from "./helpers";

export type Tenant = {
  status: string;
  id: string;
  displayName: string;
  description?: string;
  smsConfig?: {
    body?: string;
    from?: string;
  };
  routers?: Object,
  meshPods?: Object,
  sharedConfigOptions: {
    customerName: number | null;
    routerName: number | null;
    levelName: number | null;
    podName: number | null;
    wifiName: number | null;
    areaUnits: number | null;
    localeCode: string;
    countryCode: string;
    certifyId: string;
    resolveId: string;
    maxPasswordAge: number;
    maxLoginAttempts: number;
    loginTimeoutSeconds: number;
    region: string;
  },
  mobileConfig?: {
    customIdRequired: boolean;
  }
};

export function getApiBaseUrlForRegion(region: string) {
  return region === "eu-west" ? process.env.NEXT_PUBLIC_CERTIFY_API_URL_EU : process.env.NEXT_PUBLIC_CERTIFY_API_URL;
}

export function getReportsBaseUrlForRegion(region: string) {
  return region === "eu-west" ? process.env.NEXT_PUBLIC_REPORTS_URL_EU : process.env.NEXT_PUBLIC_REPORTS_URL;
}

export function useInstallations({
  tenantId,
  showAllInstalls,
  limit,
  showMinimalList,
  showLastHours,
  region
}) {
  return useInfiniteQuery(
    ["installations", { tenantId, showLastHours, region }],
    async ({ pageParam = "" }) => {
      const params: { [Key: string]: any } = {
        limit: limit,
        showAllInstalls: showAllInstalls,
        reverseOrder: true,
        lastSortKey: pageParam,
        showMinimalList: showMinimalList,
        showLastHours,
      };

      const url = getApiBaseUrlForRegion(region) + `/tenant/${tenantId}/installations`;
      const res = await makeAuthenticatedRequest(url, { data: { params } });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) => lastPage.lastSortKey || undefined,
      getPreviousPageParam: (firstPage) => firstPage.lastSortKey || undefined,
      refetchOnWindowFocus: false,
      enabled: !!region
    }
  );
}

export function useTenantCertificateBranding(tenantId, region) {
  return useQuery(
    ["certificateBranding", tenantId, region],
    async () => {
      const url =
        getApiBaseUrlForRegion(region) +
        `/tenant/${tenantId}/certificate-branding`;
      const data = await makeAuthenticatedRequest(url);
      return data.data;
    },
    {
      enabled: !!tenantId && !!region,
      refetchOnWindowFocus: false,
    }
  );
}

export function useCertifyTenant(tenantId) {
  const metersInFeet = 3.28084;

  return useQuery(['tenant', tenantId], async () => {
    const authTenantUrl = process.env.NEXT_PUBLIC_AUTH_API_URL + `/tenants/${tenantId}`;
    const authTenantRequest = await makeAuthenticatedRequest(authTenantUrl);
    const authRegion = authTenantRequest.data.region;

    const certifyTenantUrl = getApiBaseUrlForRegion(authRegion) + `/tenant/${tenantId}?exactRange=true`;
    const certifyTenantRequest = await makeAuthenticatedRequest(certifyTenantUrl);
  

    let tenantData:Tenant;
    if (certifyTenantRequest.status === 200 && authTenantRequest.status === 200) {
      tenantData = certifyTenantRequest.data;
      tenantData.sharedConfigOptions = authTenantRequest.data;

      // convert range data into feet if tenant is using imperial
      if (tenantData.sharedConfigOptions.areaUnits === 1) {
        if (Array.isArray(tenantData.routers)) {
          tenantData.routers = tenantData.routers.map((router:any) => ({ ...router, range: Math.round(router.range * metersInFeet) }))
        }
        if (Array.isArray(tenantData.meshPods)) {
          tenantData.meshPods = tenantData.meshPods.map((meshPod:any) => ({ ...meshPod, range: Math.round(meshPod.range * metersInFeet) }))
        }
      } else {
        if (Array.isArray(tenantData.routers)) {
          tenantData.routers = tenantData.routers.map((router:any) => ({ ...router, range: Math.round(router.range) }))
        }
        if (Array.isArray(tenantData.meshPods)) {
          tenantData.meshPods = tenantData.meshPods.map((meshPod:any) => ({ ...meshPod, range: Math.round(meshPod.range) }))
        }
      }

      // set default sms body
      if (!tenantData.smsConfig.body) {
        const wifiText = configOptions.wifiName[tenantData.sharedConfigOptions.wifiName] || configOptions.wifiName[0];
        tenantData.smsConfig.body = `Your ${wifiText} installation is complete and has been certified by [NAME]!\n\nView your certificate here: [LINK]`
      }
    }

    return tenantData;
  },
  {
    refetchOnWindowFocus: false,
    enabled: !!tenantId,
  })
}

export const configOptions = {
  customerName: {
    1: 'Customer',
    2: 'Subscriber',
  },
  routerName: {
    1: 'Router',
    2: 'Modem',
    3: 'Gateway',
    4: 'Hub',
    // 5: 'Residential Gateway',
  },
  levelName: {
    1: 'Level',
    2: 'Floor',
  },
  podName: {
    1: 'Mesh Pods',
    2: 'Access Points',
    3: 'Mesh Nodes',
    4: 'Mesh Pucks',
    5: 'Beacons',
    6: 'Range Extenders',
  },
  wifiName: {
    1: 'WiFi',
    2: 'Wi-Fi',
    3: 'Wifi',
    4: 'WIFI',
  },
  areaUnits: {
    1: 'Feet',
    2: 'Metres',
  },
}
